import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, BookOpen, Sparkles, Loader2, Copy, Check, Clock } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LiteratureReview = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [outline, setOutline] = useState('');
  const [outlineGeneratedAt, setOutlineGeneratedAt] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const res = await axios.get(`${API}/projects/${projectId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProject(res.data);
        // Load saved outline if exists
        if (res.data.literature_outline) {
          setOutline(res.data.literature_outline);
          setOutlineGeneratedAt(res.data.literature_outline_generated_at);
        }
      } catch (err) {
        toast.error('Failed to load project');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [projectId, token, navigate]);

  const handleGenerateOutline = async () => {
    if (!project?.title || !project?.research_aim) {
      toast.error('Project needs title and aim to generate outline');
      return;
    }
    
    setGenerating(true);
    toast.loading('Generating literature review outline...', { id: 'lit-gen' });
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 90000); // 90 second timeout
      
      const res = await axios.post(`${API}/ai/generate-literature-outline`, {
        title: project.title,
        aim: project.research_aim,
        objectives: project.research_objectives || '',
        research_type: project.research_type || 'quantitative'
      }, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!res.data?.outline) {
        throw new Error('No outline received');
      }
      
      const generatedOutline = res.data.outline;
      setOutline(generatedOutline);
      
      // Save to database
      await axios.patch(`${API}/projects/${projectId}/literature-outline`, {
        outline: generatedOutline
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setOutlineGeneratedAt(new Date().toISOString());
      toast.success('Literature review outline generated!', { id: 'lit-gen' });
    } catch (err) {
      console.error('Literature outline error:', err);
      
      if (err.name === 'AbortError' || err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        toast.error('Request timed out. Please try again.', { id: 'lit-gen' });
      } else if (err.response?.status === 500) {
        toast.error('Server error. Please try again in a moment.', { id: 'lit-gen' });
      } else {
        toast.error('Failed to generate. Please try again.', { id: 'lit-gen' });
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(outline);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate(`/project/${projectId}`)} 
                className="text-slate-500 hover:text-slate-700"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-lg font-semibold text-slate-900">Literature Review Outline</h1>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Project Context */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <BookOpen className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="font-semibold text-slate-900">Research Context</h2>
              <p className="text-sm text-slate-500">Based on your project details</p>
            </div>
          </div>
          
          <div className="space-y-3 text-sm">
            <div>
              <span className="font-medium text-slate-700">Title: </span>
              <span className="text-slate-600">{project?.title}</span>
            </div>
            <div>
              <span className="font-medium text-slate-700">Aim: </span>
              <span className="text-slate-600">{project?.research_aim}</span>
            </div>
            {project?.research_objectives && (
              <div>
                <span className="font-medium text-slate-700">Objectives: </span>
                <span className="text-slate-600 whitespace-pre-line">{project?.research_objectives}</span>
              </div>
            )}
          </div>
        </div>

        {/* Generate Button - only show if no outline exists */}
        {!outline && (
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 rounded-xl p-8 text-center mb-6">
            <Sparkles className="w-12 h-12 text-indigo-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Generate Literature Review Outline</h3>
            <p className="text-slate-600 mb-6 max-w-lg mx-auto">
              Get a detailed outline for your literature review based on your research title, aim, and objectives. 
              The outline will include suggested sections, themes, and keywords for your research.
            </p>
            <Button 
              onClick={handleGenerateOutline} 
              disabled={generating}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="generate-outline-btn"
            >
              {generating ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating Outline...</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" /> Generate Outline</>
              )}
            </Button>
          </div>
        )}

        {/* Generated/Saved Outline */}
        {outline && (
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-slate-900">Literature Review Outline</h3>
                {outlineGeneratedAt && (
                  <p className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                    <Clock className="w-3 h-3" />
                    Generated: {new Date(outlineGeneratedAt).toLocaleString()}
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleCopy}
                  data-testid="copy-outline-btn"
                >
                  {copied ? (
                    <><Check className="w-4 h-4 mr-1" /> Copied</>
                  ) : (
                    <><Copy className="w-4 h-4 mr-1" /> Copy</>
                  )}
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleGenerateOutline}
                  disabled={generating}
                  data-testid="regenerate-btn"
                >
                  {generating ? (
                    <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Regenerating...</>
                  ) : (
                    <><Sparkles className="w-4 h-4 mr-1" /> Regenerate</>
                  )}
                </Button>
              </div>
            </div>
            
            <div className="prose prose-slate max-w-none">
              <pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans bg-slate-50 p-4 rounded-lg border">
                {outline}
              </pre>
            </div>
            
            <p className="text-xs text-slate-500 mt-4">
              This outline is AI-generated and automatically saved. Regenerating will overwrite the current outline.
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default LiteratureReview;
